import { useEffect, useRef, useState } from 'react'

const REVEAL_FALLBACK_MS = 2000

/** True when any part of the element is in the viewport. */
function isInViewport(el: HTMLElement) {
  const rect = el.getBoundingClientRect()
  const vh = window.innerHeight || document.documentElement.clientHeight
  const vw = window.innerWidth || document.documentElement.clientWidth
  return rect.bottom > 0 && rect.right > 0 && rect.top < vh && rect.left < vw
}

/**
 * Adds reveal class once when element enters viewport; unobserve after first reveal.
 * Always reveals for reduced motion, if already on-screen, or after a short fallback
 * so content never stays visually blank.
 */
export function useRevealOnce<T extends HTMLElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null)
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || revealed) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion || isInViewport(el)) {
      setRevealed(true)
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true)
            io.unobserve(entry.target)
          }
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    )

    io.observe(el)

    const fallback = window.setTimeout(() => setRevealed(true), REVEAL_FALLBACK_MS)

    return () => {
      io.disconnect()
      window.clearTimeout(fallback)
    }
  }, [revealed, threshold])

  return { ref, revealed }
}
